import { useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import TextButton from 'LuxKit/Button/TextButton'
import ClassicRewardsSealIcon from 'LuxKit/Icons/loyalty/ClassicRewardsSealIcon'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import useLoyaltyPointsPurchaseBestOffer from 'loyalty/hooks/useLoyaltyPointsPurchaseBestOffer'
import useOpenCharterFareClassModal from 'hooks/OfferPage/useOpenCharterFareClassModal'
import { useCharterFilterUrlState } from 'hooks/CharterFlights/useCharterFilterUrlState'
import type { FareClassWithReward } from 'constants/flight'

const Card = styled(Pane)`
  border-radius: ${(props) => props.theme.borderRadius.S};
  padding: ${rem(16)};
  text-align: center;
`

interface OwnProps {
  rewardPointsDeficit: number
}

interface MappedProps {
  offer?: App.Offer
  fareClass: FareClassWithReward
  checkInDate?: string
  checkOutDate?: string
  occupancies: Array<App.Occupants>
}

type Props = OwnProps & MappedProps

/**
 * Card shown when the member has a rewards fare
 * selected but not enough loyalty/societe points to redeem it.
 */
function FlashHotelInsufficientLoyaltyPointsCard({ rewardPointsDeficit, offer, fareClass, checkInDate, checkOutDate, occupancies }: Props) {
  const intl = useIntl()
  const loyaltyProgramName = getLoyaltyProgramDisplayName()
  const { canOpen, open } = useOpenCharterFareClassModal({ offer, fareClass, checkInDate, checkOutDate, occupancies })
  const { offer: bestPurchaseOffer } = useLoyaltyPointsPurchaseBestOffer(rewardPointsDeficit)
  const buyPointsHref = bestPurchaseOffer
    ? `/societe/buy-points?offerId=${encodeURIComponent(bestPurchaseOffer.id)}`
    : '/societe/buy-points'
  const { setRewardSeatsOnly } = useCharterFilterUrlState()

  // "View other options" exists to let the user escape the reward filter when they don't
  // have enough points — flip `?societeRewards=` to false AND pass an explicit override into
  // `open(...)` so the modal mounts with the toggle off, even though the parent's
  // `rewardSeatsOnly` closure is still stale at this synchronous call site.
  const handleViewOtherOptionsClick = useCallback(() => {
    setRewardSeatsOnly(false)
    open({ rewardSeatsOnly: false })
  }, [setRewardSeatsOnly, open])

  return (
    <Card background="light-background">
      <Group direction="vertical" gap={8}>
        <Group direction="horizontal" verticalAlign="center" horizontalAlign="center" gap={4}>
          <ClassicRewardsSealIcon size={20} colour="highlight-primary-dark" aria-hidden />
          <BodyText variant="medium" weight="bold" colour="highlight-primary-dark" as="span">
            {intl.formatMessage(
              {
                id: 'HK_aIz5NHfsk81_smQ0n',
                defaultMessage: '{loyaltyProgramName} Rewards',
                description: 'Heading of the insufficient-points card on the Flash hotel offer page; the configured loyalty program name (e.g. Société) followed by "Rewards".',
              },
              { loyaltyProgramName },
            )}
          </BodyText>
        </Group>
        <BodyText variant="small" colour="neutral-one">
          <FormattedMessage
            id="T-O-nWnp1KqxvP4l28Xe"
            defaultMessage="You require an additional {points} to book this package."
            description="Message shown on offer page package options when the user does not have enough loyalty points to book with a reward fare class."
            values={{ points: <FormatLoyaltyPoints points={rewardPointsDeficit} /> }}
          />
        </BodyText>
        <TextButton kind="primary" fit="full-width" to={buyPointsHref} size="small" target="_blank">
          {intl.formatMessage(
            {
              id: '87a9q3FLljsEErQFggqK',
              defaultMessage: 'Top-up {loyaltyProgramName} Points',
              description: 'Primary CTA on the insufficient-points card on the Flash hotel offer page; navigates to the loyalty buy-points page.',
            },
            { loyaltyProgramName },
          )}
        </TextButton>
        {canOpen && (
          <>
            <Caption variant="medium" colour="neutral-two">
              {intl.formatMessage({
                id: 'Chrk1O53GPR6pKXkK7j1',
                defaultMessage: 'or',
                description: 'Separator between the primary and secondary CTAs on the insufficient-points card on the Flash hotel offer page.',
              })}
            </Caption>
            <TextButton kind="secondary" fit="full-width" onClick={handleViewOtherOptionsClick} size="small">
              {intl.formatMessage({
                id: '2LeFYRhZ7kg-G7Zqi5ME',
                defaultMessage: 'View other options',
                description: 'Secondary CTA on the insufficient-points card on the Flash hotel offer page; opens the fare-class selection modal so members can pick a non-reward fare.',
              })}
            </TextButton>
          </>
        )}
      </Group>
    </Card>
  )
}

export default withOfferPageState((state): MappedProps => ({
  offer: state.offer,
  fareClass: state.fareClass,
  checkInDate: state.checkInDate,
  checkOutDate: state.checkOutDate,
  occupancies: state.rooms,
}), FlashHotelInsufficientLoyaltyPointsCard)
